import { useEffect, useState } from "react";
import { Loader2, Trophy, Globe, Building } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { trashLeaderboard } from "@/lib/trashApi";

export default function TrashLeaderboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("alltime");
  const [scope, setScope] = useState("global");
  const [data, setData] = useState(null);

  useEffect(() => { trashLeaderboard({ period, scope, limit: 50 }).then(setData).catch(() => setData({ leaderboard: [], me: null })); }, [period, scope]);

  return (
    <div className="space-y-4" data-testid="trash-leaderboard">
      <section className="rounded-3xl border-2 border-[#FFD700]/40 bg-gradient-to-br from-[#FFD700]/10 to-transparent p-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-6 h-6 text-[#FFD700]" />
          <div className="flex-1">
            <div className="font-black text-xl">Leaderboards</div>
            <div className="text-[10px] mono uppercase tracking-widest text-white/50">Top cleaners by XP</div>
          </div>
        </div>
      </section>

      <div className="space-y-2">
        <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
          {[["today","Today"],["week","Week"],["alltime","All-time"]].map(([p,l]) => (
            <button key={p} data-testid={`board-period-${p}`} onClick={() => setPeriod(p)}
              className={"flex-1 py-2 text-xs uppercase tracking-widest font-black rounded-full " + (period === p ? "bg-[#39FF14] text-black" : "text-white/60")}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
          <button data-testid="board-scope-global" onClick={() => setScope("global")}
            className={"flex-1 py-2 text-xs uppercase tracking-widest font-black rounded-full inline-flex items-center gap-1 justify-center " + (scope === "global" ? "bg-[#39FF14] text-black" : "text-white/60")}>
            <Globe className="w-3 h-3" /> Global
          </button>
          <button data-testid="board-scope-city" onClick={() => { const c = prompt("City name (lowercase, e.g. 'chicago')"); if (c) setScope(c.trim().toLowerCase()); }}
            className={"flex-1 py-2 text-xs uppercase tracking-widest font-black rounded-full inline-flex items-center gap-1 justify-center " + (scope !== "global" ? "bg-[#39FF14] text-black" : "text-white/60")}>
            <Building className="w-3 h-3" /> {scope === "global" ? "City" : scope}
          </button>
        </div>
      </div>

      {!data ? <div className="py-8 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> :
        data.leaderboard.length === 0 ? <div className="py-12 text-center text-white/50">No entries yet. Be the first!</div> :
        <ul className="space-y-1" data-testid="board-list">
          {data.leaderboard.map((r) => (
            <li key={r.user_id} data-testid={`board-row-${r.user_id}`}
              className={"flex items-center gap-3 rounded-2xl p-3 border " + (data.me?.user_id === r.user_id ? "border-[#39FF14]/50 bg-[#39FF14]/10" : "border-white/10 bg-black/40")}>
              <div className={"w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm " + (r.rank <= 3 ? "bg-[#FFD700] text-black" : "bg-white/10")}>{r.rank}</div>
              <div className="w-8 h-8 rounded-full shrink-0" style={{ background: r.avatar_color || "#00F0FF" }} />
              <div className="min-w-0 flex-1">
                <div className="font-black text-sm truncate">{r.user_name}</div>
                <div className="text-[10px] text-white/50">{r.cleanups} cleanups · {r.coins} coins</div>
              </div>
              <div className="font-black text-lg tabular-nums text-[#39FF14]">{r.xp?.toLocaleString?.() || 0}</div>
            </li>
          ))}
        </ul>}

      {data?.me && data.me.rank > (data.leaderboard[data.leaderboard.length - 1]?.rank || 0) && (
        <div className="rounded-2xl border-2 border-[#39FF14]/40 bg-[#39FF14]/10 p-3 flex items-center gap-3" data-testid="board-me-row">
          <div className="w-8 h-8 rounded-lg bg-[#39FF14] text-black flex items-center justify-center font-black text-sm">{data.me.rank}</div>
          <div className="min-w-0 flex-1">
            <div className="font-black text-sm">You</div>
            <div className="text-[10px] text-white/60">{data.me.cleanups} cleanups</div>
          </div>
          <div className="font-black text-lg tabular-nums text-[#39FF14]">{data.me.xp?.toLocaleString?.() || 0}</div>
        </div>
      )}
    </div>
  );
}
